import React, { useMemo, useState } from "react";

import {
  Banner,
  Button,
  Card,
  Modal,
  ResourceItem,
  ResourceList,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import ImportGroupingPickerModal from "./ImportGroupingPickerModal";
import WeightDescriptorEditor, {
  descriptorsAreValid,
} from "./WeightDescriptorEditor";
import {
  cleanGroupingOverrideForSave,
  normalizeGroupingKey,
} from "./weightOverridePayloads";

/**
 * Sub-department default weights section for the Weight Options page.
 *
 * Lists overrides stored under `shopConfig.globepos_subdepartment_variant_weights`
 * and provides Add / Edit / Remove flows that funnel through:
 *
 *   ImportGroupingPickerModal  →  EditorModal (label + WeightDescriptorEditor)
 *                                      ↓
 *                             onSave({ globepos_subdepartment_variant_weights })
 *
 * Save is blocked when the normalized key is blank — the backend sanitizer
 * silently drops blank-keyed entries, so the UI must prevent the false
 * "saved successfully" UX (spec rule).
 *
 * Props
 * -----
 * @param {object}   shopConfig - The shop_config object.  Reads
 *                                `globepos_subdepartment_variant_weights`;
 *                                missing or non-object values are treated as {}.
 * @param {number}   shopId     - Used by the picker's fetch.
 * @param {function} onSave     - Called with a partial shop_config object
 *                                (always under the key
 *                                `globepos_subdepartment_variant_weights`).
 *                                May return a Promise.
 */
export default function SubdepartmentVariantWeightsSection({
  shopConfig,
  shopId,
  onSave,
}) {
  const overrides = useMemo(() => {
    const raw = shopConfig && shopConfig.globepos_subdepartment_variant_weights;
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  }, [shopConfig]);

  const entries = useMemo(
    () =>
      Object.entries(overrides)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => ({
          key,
          label: (value && value.label) || key,
          code: (value && value.code) || null,
          descriptors: Array.isArray(value && value.descriptors)
            ? value.descriptors
            : [],
        })),
    [overrides],
  );

  // ---- Picker state ---------------------------------------------------------
  const [pickerOpen, setPickerOpen] = useState(false);

  // ---- Editor state ---------------------------------------------------------
  // `activeEntry` is { key, label, code, descriptors, isNew }.
  const [activeEntry, setActiveEntry] = useState(null);
  const [editorLabel, setEditorLabel] = useState("");
  const [editorDescriptors, setEditorDescriptors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ---- Remove confirmation state -------------------------------------------
  const [confirmingDeleteKey, setConfirmingDeleteKey] = useState(null);

  // ---- Picker → editor wiring ----------------------------------------------
  const handlePickerSelect = ({ key, label, code }) => {
    setPickerOpen(false);
    // If the merchant picked an entry that's already configured, seed the
    // editor from the existing override so they edit instead of clobbering.
    const existing = overrides[key];
    setActiveEntry({
      key,
      label: label || key,
      code: code || (existing && existing.code) || null,
      descriptors:
        existing && Array.isArray(existing.descriptors)
          ? existing.descriptors
          : [{ weight: "", description: "" }],
      isNew: !existing,
    });
    setEditorLabel(existing ? existing.label || label || key : label || key);
    setEditorDescriptors(
      existing && Array.isArray(existing.descriptors) && existing.descriptors.length > 0
        ? existing.descriptors.map((d) => ({
            weight: String(d.weight || ""),
            description: String(d.description || ""),
          }))
        : [{ weight: "", description: "" }],
    );
    setSaving(false);
    setSaveError(null);
  };

  const handleEditEntry = (entry) => {
    setActiveEntry({
      key: entry.key,
      label: entry.label,
      code: entry.code,
      descriptors: entry.descriptors,
      isNew: false,
    });
    setEditorLabel(entry.label);
    setEditorDescriptors(
      entry.descriptors.length > 0
        ? entry.descriptors.map((d) => ({
            weight: String(d.weight || ""),
            description: String(d.description || ""),
          }))
        : [{ weight: "", description: "" }],
    );
    setSaving(false);
    setSaveError(null);
  };

  const closeEditor = () => {
    if (saving) return;
    setActiveEntry(null);
    setEditorLabel("");
    setEditorDescriptors([]);
    setSaveError(null);
  };

  // ---- Save flow -----------------------------------------------------------
  // Blank-key guard: the normalized key is what the backend uses; if it's
  // empty the sanitizer silently drops the entry, so we hard-block save.
  const normalizedEditorKey = normalizeGroupingKey(
    editorLabel || (activeEntry && activeEntry.key) || "",
  );
  const editorKeyIsBlank = normalizedEditorKey === "";
  const descriptorsValid = descriptorsAreValid(editorDescriptors);
  const canSave = !editorKeyIsBlank && descriptorsValid;

  const handleSaveEditor = () => {
    if (!canSave) return;
    setSaveError(null);

    const [key, payload] = cleanGroupingOverrideForSave({
      key: activeEntry.key || editorLabel,
      label: editorLabel,
      code: activeEntry.code,
      descriptors: editorDescriptors,
      includeParent: false,
    });

    // The picker may have surfaced the entry under one normalized key
    // (e.g. "CAFE") while the merchant typed a different label — the
    // normalized label wins so we don't end up with duplicate or stale
    // entries under the previous key.
    const previousKey = activeEntry.isNew ? null : activeEntry.key;
    const nextOverrides = { ...overrides };
    if (previousKey && previousKey !== key) {
      delete nextOverrides[previousKey];
    }
    nextOverrides[key] = payload;

    const result = onSave({
      globepos_subdepartment_variant_weights: nextOverrides,
    });

    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          closeEditor();
        })
        .catch((err) => {
          setSaving(false);
          setSaveError(
            (err && err.message) ||
              "An error occurred while saving.  Please try again.",
          );
        });
    } else {
      closeEditor();
    }
  };

  // ---- Remove flow ---------------------------------------------------------
  const handleRemove = (key) => {
    setConfirmingDeleteKey(key);
  };

  const closeRemoveDialog = () => {
    if (saving) return;
    setConfirmingDeleteKey(null);
  };

  const confirmRemove = () => {
    if (!confirmingDeleteKey) return;
    const nextOverrides = { ...overrides };
    delete nextOverrides[confirmingDeleteKey];
    const result = onSave({
      globepos_subdepartment_variant_weights: nextOverrides,
    });
    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          setConfirmingDeleteKey(null);
        })
        .catch(() => {
          setSaving(false);
        });
    } else {
      setConfirmingDeleteKey(null);
    }
  };

  // ---- Render helpers ------------------------------------------------------
  const renderEntry = (entry) => {
    const descriptorLabel =
      entry.descriptors.length > 0
        ? entry.descriptors
            .map((d) =>
              d.description ? `${d.weight} - ${d.description}` : d.weight,
            )
            .join(", ")
        : "No weight tiers";

    return (
      <ResourceItem
        id={entry.key}
        accessibilityLabel={`Edit ${entry.label}`}
      >
        <Stack alignment="center" distribution="equalSpacing" wrap={false}>
          <Stack.Item fill>
            <Stack vertical spacing="extraTight">
              <TextStyle variation="strong">{entry.label}</TextStyle>
              <TextStyle variation="subdued">
                Sub-department: {entry.key}
                {entry.code ? ` (${entry.code})` : ""}
              </TextStyle>
              <TextStyle variation="subdued">{descriptorLabel}</TextStyle>
            </Stack>
          </Stack.Item>
          <Stack spacing="tight">
            <Button size="slim" onClick={() => handleEditEntry(entry)}>
              Edit
            </Button>
            <Button
              size="slim"
              destructive
              onClick={() => handleRemove(entry.key)}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      </ResourceItem>
    );
  };

  return (
    <>
      <Card>
        <Card.Header
          title="Sub-department defaults"
          actions={[
            {
              content: "Add sub-department override",
              onAction: () => setPickerOpen(true),
            },
          ]}
        />
        <Card.Section>
          <TextStyle variation="subdued">
            Defaults applied to weighted items in this sub-department during
            import.  Per-item PLU overrides still win.
          </TextStyle>
        </Card.Section>
        {entries.length === 0 ? (
          <Card.Section>
            <TextStyle variation="subdued">
              No sub-department overrides configured.
            </TextStyle>
          </Card.Section>
        ) : (
          <ResourceList
            resourceName={{
              singular: "sub-department override",
              plural: "sub-department overrides",
            }}
            items={entries}
            renderItem={renderEntry}
          />
        )}
      </Card>

      <ImportGroupingPickerModal
        open={pickerOpen}
        mode="subdepartment"
        shopId={shopId}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        existingKeys={Object.keys(overrides)}
      />

      <Modal
        large
        open={Boolean(activeEntry)}
        title={
          activeEntry && activeEntry.isNew
            ? "Add sub-department override"
            : "Edit sub-department override"
        }
        onClose={closeEditor}
        primaryAction={{
          content: "Save",
          onAction: handleSaveEditor,
          loading: saving,
          disabled: !canSave,
        }}
        secondaryActions={[{ content: "Cancel", onAction: closeEditor }]}
      >
        <Modal.Section>
          <TextField
            label="Label"
            value={editorLabel}
            onChange={setEditorLabel}
            autoComplete="off"
            error={
              editorKeyIsBlank
                ? "Label is required (it becomes the sub-department match key)."
                : undefined
            }
            helpText="Used to match SUB DEPARTMENT NAME during GlobePOS import. Stored uppercase."
          />
        </Modal.Section>

        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextStyle variation="strong">Weight tiers</TextStyle>
            <WeightDescriptorEditor
              descriptors={editorDescriptors}
              onChange={setEditorDescriptors}
            />
          </Stack>
        </Modal.Section>

        {saveError && (
          <Modal.Section>
            <Banner status="critical">{saveError}</Banner>
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={Boolean(confirmingDeleteKey)}
        title="Remove sub-department override"
        onClose={closeRemoveDialog}
        primaryAction={{
          content: "Remove override",
          destructive: true,
          onAction: confirmRemove,
          loading: saving,
        }}
        secondaryActions={[{ content: "Cancel", onAction: closeRemoveDialog }]}
      >
        <Modal.Section>
          <TextStyle>
            This sub-department will use the next applicable tier on the next
            import.
          </TextStyle>
        </Modal.Section>
      </Modal>
    </>
  );
}
